import { ProfileDto, ProfileSearchDto, ProfileSearchType } from '@inosio/payments-sdk';
import { FormControl, Grid, InputLabel, Link, MenuItem, Paper, Select, SelectChangeEvent, Stack, Box, Checkbox, ListItemText, OutlinedInput, Button } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip } from '@mui/material';
import React, { KeyboardEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LogoutIcon from '@mui/icons-material/Logout';
// import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { clearCredentials } from '../../shared/sagas/auth/authSlice';
import { getProfiles, selectProfileHistory, selectAdminState, selectProfileHistoryCurrentPage } from '../sagas/adminPortal.slice';
import { HashLoader } from 'react-spinners';
import { css } from '@emotion/react';
import CopyToClipboard from 'react-copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

enum SearchFieldTypes {
  Name = 'Name',
  Email = 'Email',
  PropertyAddress = 'PropertyAddress',
  Company = 'Company',
  Uuid = 'Uuid',
}

export default function AdminProfiles() {
  const dispatch = useDispatch();
  const [searchField, setSearchField] = React.useState<string>(SearchFieldTypes.Name);
  const [searchValue, setSearchValue] = React.useState('');
  const [rangeSize, setRangeSize] = React.useState(20);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [startIndex, setStartIndex] = React.useState(0);
  const [openToolTip, setOpenToolTip] = React.useState(null);
  const { isProfilesFetching } = useSelector(selectAdminState);
  const profileData = useSelector(selectProfileHistory);
  const profileDataBook = useSelector(selectProfileHistoryCurrentPage);

  const availableProfileTypes = Object.keys(ProfileSearchType).map((key) => {
    return key;
  });
  const [profileTypes, setProfileTypes] = React.useState<string[]>(availableProfileTypes);

  const handleEntityTypeChange = (event: SelectChangeEvent<typeof profileTypes>) => {
    const {
      target: { value },
    } = event;
    setProfileTypes(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setCurrentPage(newPageNumber);
    setStartIndex(+newPageNumber * +rangeSize);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const onToggleClick = (index: any) => {
    setOpenToolTip(openToolTip === index ? null : index);
  };

  const handleSearch = () => {
    //only invoke search if there's enough criteria
    if (searchField && searchValue) {
      dispatch({
        type: getProfiles.type,
        payload: {
          Name: searchField === SearchFieldTypes.Name ? searchValue : undefined,
          Email: searchField === SearchFieldTypes.Email ? searchValue : undefined,
          PropertyAddress: searchField === SearchFieldTypes.PropertyAddress ? searchValue : undefined,
          Company: searchField === SearchFieldTypes.Company ? searchValue : undefined,
          Uuid: searchField === SearchFieldTypes.Uuid ? searchValue : undefined,
          ProfileTypes: profileTypes?.length ? profileTypes : undefined,
          StartIndex: startIndex,
          RangeSize: rangeSize,
        } as ProfileSearchDto,
      });
    }
  };

  useEffect(() => {
    handleSearch();
  }, [rangeSize, startIndex]);

  const override = css`
    display: block;
    margin: 15% auto auto;
  `;

  return (
    <Stack sx={{ position: 'relative' }}>
      {/* show this when ui is fetching data */}
      {isProfilesFetching && (
        <Box sx={{ height: '100%', width: '100%', position: 'absolute', top: '10%', backgroundColor: '#fff', opacity: '0.5', zIndex: 1000 }}>
          <HashLoader color={'#BABABA'} loading={isProfilesFetching} size={150} css={override} />
        </Box>
      )}

      <Grid container>
        <Grid item>
          <FormControl sx={{ m: 1, width: 400 }} size="small">
            <InputLabel id="profile-type-checkbox-label">Profile Type(s)</InputLabel>
            <Select
              labelId="profile-type-checkbox-label"
              id="profile-type-checkbox"
              multiple
              value={profileTypes}
              onChange={handleEntityTypeChange}
              input={<OutlinedInput label="Profile Type(s)" />}
              renderValue={(selected) => selected.join(', ')}
            >
              {availableProfileTypes.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={profileTypes.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ m: 1, ml: 4, width: 200 }} size="small">
            <InputLabel>Search By</InputLabel>
            <Select value={searchField} label="Search By" onChange={(e: SelectChangeEvent) => setSearchField(e.target.value)}>
              {(Object.keys(SearchFieldTypes) as (keyof typeof SearchFieldTypes)[]).map((searchField, index) => {
                return (
                  <MenuItem value={SearchFieldTypes[searchField]} key={index}>
                    {SearchFieldTypes[searchField]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl sx={{ m: 1, ml: 3, width: 300, '.MuiInputBase-input': { paddingBottom: '8px' }, '.MuiInputLabel-root': { marginTop: '-6px' }, '.MuiInputBase-root': { marginTop: '8px' } }}>
            <TextField
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  setCurrentPage(0);
                  setStartIndex(0);
                  handleSearch();
                }
              }}
              label="value"
              variant="standard"
              size="small"
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={() => {
              setCurrentPage(0);
              setStartIndex(0);
              handleSearch();
            }}
          >
            Search
          </Button>
        </Grid>
        <Grid>
          {/* <Tooltip title="reset selection">
            <Link
              href="#"
              color="inherit"
              onClick={() => {
                setSearchField(SearchFieldTypes.Name);
                setSearchValue('');
              }}
              sx={{ display: 'block', marginTop: '16px' }}
            >
              <RestartAltIcon />
            </Link>
          </Tooltip> */}
        </Grid>
        <Grid item sx={{ marginLeft: 'auto', paddingRight: '2%' }}>
          <Box>
            <Tooltip title="logout">
              <Link
                href="#"
                color="inherit"
                onClick={() => {
                  dispatch(clearCredentials());
                }}
                sx={{ display: 'block', marginTop: '16px' }}
              >
                <LogoutIcon />
              </Link>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ px: 4, py: 2 }}>
        <TableContainer component={Paper} sx={{ borderRadius: '4px', border: '1px solid #E8E8E8' }}>
          <Table sx={{ minWidth: 650, marginTop: '10px' }} aria-label="simple table">
            <TableHead sx={{ '.MuiTableCell-root': { fontWeight: 800, fontSize: '1rem' }, mt: 0 }}>
              <TableRow>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Property Address</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ '.MuiTableCell-root': { padding: '8px 16px 12px' } }}>
              {profileDataBook?.[currentPage]?.Rows?.map((entity: ProfileDto, index) => (
                <TableRow key={entity.Uuid} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>{`${entity.FirstName.charAt(0).toUpperCase()}${entity.FirstName.slice(1).toLowerCase()}`}</TableCell>
                  <TableCell>{`${entity.LastName.charAt(0).toUpperCase()}${entity.LastName.slice(1).toLowerCase()}`}</TableCell>
                  <TableCell>{entity.Type}</TableCell>
                  <TableCell>{entity.PropertyAddress}</TableCell>
                  <TableCell>{entity.Company}</TableCell>
                  <TableCell>{entity.Email}</TableCell>
                  <TableCell>
                    <Grid container display="flex" alignItems="stretch" columnGap={1}>
                      <Grid item>
                        <CopyToClipboard text={entity.Uuid}>
                          <Tooltip title={'Copied'} open={openToolTip === index} onClose={() => onToggleClick(null)} onClick={() => onToggleClick(index)} sx={{ cursor: 'pointer' }}>
                            <ContentCopyIcon fontSize="small" />
                          </Tooltip>
                        </CopyToClipboard>
                      </Grid>
                      <Tooltip title={entity.Uuid}>
                        <Link
                          href="#"
                          underline="hover"
                          color="inherit"
                          onClick={() => {
                            window.open(`/login?logAsEntityUuid=${entity.Uuid}&logAsUserUuid=${entity.UserUuid||''}`);
                          }}
                        >{`${entity.Uuid.slice(0, 7)}...`}</Link>
                      </Tooltip>
                    </Grid>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            rowsPerPageOptions={[20, 30, 40]}
            count={profileData.TotalCount || 0}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </Stack>
  );
}
